import type { TrainingSession } from '@cadencia/contracts';
import { assessWeekClose } from '@cadencia/domain';
import { useState, type ReactNode } from 'react';
import { Boton } from '../../components/ui.js';
import { useAppData } from '../../data/DataProvider.js';
import { closeWeek } from '../../data/services/weekCloseService.js';
import { formatShortDate } from '../../lib/dates.js';
import { mensajeDeError } from '../../lib/errores.js';
import './cerrar-semana.css';

/**
 * «Dar por terminada la semana» (decisión del usuario, 13/09/2026).
 *
 * Dos pasos y no uno: marca sesiones como no hechas, y eso cuenta en la
 * revisión semanal y en la puntuación de descarga. Un toque suelto no puede
 * hacer eso. La confirmación NOMBRA las sesiones —«Pierna A · dom 13»—, porque
 * «¿seguro?» a secas se contesta sin leer.
 *
 * No es un modal: va en su sitio, debajo de la lista de la semana, que es donde
 * se ve qué queda.
 */
export function CerrarSemana({
  sesiones,
  onCerrada,
}: {
  sesiones: readonly TrainingSession[];
  /** Recibe la frase que resume lo que ha pasado, para pintarla arriba. */
  onCerrada: (texto: string) => void;
}): ReactNode {
  const { ctx, refreshPending } = useAppData();
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { closable, inProgress } = assessWeekClose(sesiones);
  if (ctx === null || closable.length === 0) return null;

  if (inProgress.length > 0) {
    return (
      <p className="campo__pista">
        Para dar por terminada la semana, termina antes la sesión que tienes a medias.
      </p>
    );
  }

  if (!confirmando) {
    return (
      <Boton variante="secundario" className="cerrar-semana__abrir" onClick={() => setConfirmando(true)}>
        Dar por terminada la semana
      </Boton>
    );
  }

  const cerrar = (): void => {
    setOcupado(true);
    setError(null);
    void (async () => {
      try {
        const resultado = await closeWeek(ctx);
        if (resultado.reasonCodes.includes('session_in_progress')) {
          setError('Hay una sesión a medias: termínala antes de cerrar la semana.');
          return;
        }
        const cuantas = resultado.skipped.length;
        const saltadas =
          cuantas === 1 ? 'Una sesión queda como «No se hará».' : `${String(cuantas)} sesiones quedan como «No se hará».`;
        onCerrada(
          resultado.nextWeek === null
            ? `Semana cerrada. ${saltadas} Era la última del bloque: el siguiente se decide al empezar la semana.`
            : `Semana cerrada. ${saltadas} La semana ${String(resultado.nextWeek.weekIndex)} ya está preparada.`,
        );
        await refreshPending();
      } catch (caught) {
        setError(`No he podido cerrar la semana: ${mensajeDeError(caught)}`);
      } finally {
        setOcupado(false);
      }
    })();
  };

  return (
    <div className="cerrar-semana" role="group" aria-labelledby="cerrar-semana-titulo">
      <p id="cerrar-semana-titulo" className="cerrar-semana__titulo">
        ¿Dar por terminada la semana?
      </p>
      <p className="campo__pista">
        {closable.length === 1 ? 'Esta sesión quedará' : 'Estas sesiones quedarán'} como «No se
        hará»:
      </p>
      <ul className="cerrar-semana__lista">
        {closable.map((sesion) => (
          <li key={sesion.id}>
            {sesion.name} · {formatShortDate(sesion.scheduledDate)}
          </li>
        ))}
      </ul>
      <p className="campo__pista">
        Cuentan como no hechas en la revisión semanal y no se pasan a otro día. Después se prepara la
        semana que viene.
      </p>
      {error !== null && (
        <p className="campo__error" role="alert">
          {error}
        </p>
      )}
      <div className="cerrar-semana__acciones">
        <Boton onClick={cerrar} cargando={ocupado}>
          {ocupado ? 'Cerrando…' : 'Terminar la semana'}
        </Boton>
        <Boton variante="texto" onClick={() => setConfirmando(false)} disabled={ocupado}>
          Cancelar
        </Boton>
      </div>
    </div>
  );
}
